import { useEffect } from "react";
import type { ThemeDescriptor } from "@/lib/api";
import type { ResolvedTables } from "@/lib/themes/look";
import { loadThemeFont } from "@/lib/themes/fonts";
import { bandedRow, densityScale, paperTableStyle } from "@/lib/themes/tables";
import { testIds } from "@/testIds";

/** The sample: three columns, five body rows, numbers on the right. */
const HEAD = ["Model", "Params", "Top-1"];
const ROWS = [
  ["ResNet-50", "25.6 M", "76.1"],
  ["ViT-B/16", "86.6 M", "81.8"],
  ["ConvNeXt-T", "28.6 M", "82.1"],
  ["Swin-T", "28.3 M", "81.3"],
  ["Ours", "24.1 M", "82.6"],
];

/**
 * Look → Tables, previewed live (2f): a small table drawn the way this paper
 * prints its tables, on the paper, in the theme's colours. Index draws it in
 * the first chapter colour, as a table in chapter 1 would print.
 */
export function TableStyleSample({
  theme,
  tables,
  paper,
}: {
  theme: ThemeDescriptor;
  tables: ResolvedTables;
  paper: "theme" | "white";
}) {
  const s = paperTableStyle(theme, tables, { paper, sectionColour: theme.sequence?.[0] ?? null });
  const scale = densityScale(s.density);
  const heading = theme.fonts.heading;
  const body = theme.fonts.body;
  useEffect(() => {
    void loadThemeFont(heading);
    void loadThemeFont(body);
  }, [heading, body]);

  const fontPx = 11 * scale.font;
  const cellPad = `${Math.round(3 * scale.row)}px ${Math.round(8 * scale.padX)}px`;
  const caption = (
    <div style={{ fontSize: fontPx, margin: s.caption === "above" ? "0 0 5px" : "5px 0 0" }}>
      <b style={{ color: s.label, fontFamily: s.labelFont }}>Table 1.</b> Top-1 accuracy.
    </div>
  );

  return (
    <div
      data-testid={testIds.look.tablesSample}
      data-style={s.style}
      data-density={s.density}
      data-caption={s.caption}
      aria-hidden="true"
      className="rounded-[5px] px-3 py-2.5"
      style={{
        background: s.paper,
        color: s.ink,
        fontFamily: s.bodyFont,
        boxShadow: "inset 0 0 0 1px rgba(16,24,40,.10)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {s.caption === "above" && caption}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: fontPx,
          borderTop: s.rule ? `1.5px solid ${s.rule}` : undefined,
          borderBottom: `1px solid ${s.ink}`,
        }}
      >
        <thead>
          <tr
            data-part="head"
            style={{
              background: s.headFill ?? undefined,
              color: s.headInk,
              fontFamily: s.headFont,
              fontWeight: s.headWeight ?? 600,
              borderBottom: s.headFill ? undefined : `0.75px solid ${s.ink}`,
            }}
          >
            {HEAD.map((h, c) => (
              <th key={c} scope="col" style={{ padding: cellPad, textAlign: c === 0 ? "left" : "right", fontWeight: "inherit" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row, r) => (
            <tr key={r} data-band={s.band && bandedRow(r, ROWS.length) ? "true" : undefined} style={{ background: s.band && bandedRow(r, ROWS.length) ? s.band : undefined }}>
              {row.map((v, c) => (
                <td key={c} style={{ padding: cellPad, textAlign: c === 0 ? "left" : "right" }}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {s.caption === "below" && caption}
    </div>
  );
}
